import { createUsageProfiler } from './usageProfiler';
import { createUsageRepository } from './usageRepository';

// One profiler per mounted workspace. Component adapters never send network requests.
export function mountUsageRuntime({ uid, experience, root, readState, readPanels, onReady }) {
  let disposed = false, profiler = null, timer = null, ticks = 0, lastActivity = 0;
  const cleanups = [];
  let pendingDiagnostics = [];
  const safe = fn => (...args) => { try { return fn(...args); } catch (_) { /* Best effort. */ } };
  const refresh = safe(() => {
    if (!profiler) return;
    profiler.snapshot(readState());
    const panels = readPanels();
    Object.keys(panels).forEach(surface => profiler.panel(surface, panels[surface]));
  });
  const activity = safe(() => {
    if (Date.now() - lastActivity < 1000) return;
    lastActivity = Date.now(); profiler.activity();
  });
  const listen = (target, name, handler, options = { passive: true, capture: true }) => {
    target.addEventListener(name, handler, options);
    cleanups.push(() => target.removeEventListener(name, handler, options));
  };
  // Read only explicit machine labels, never DOM text, values or key contents.
  const interaction = safe(event => {
    if (!event.target.closest) return;
    const target = event.target.closest('[data-usage-action]');
    if (target) {
      activity();
      profiler.track('interaction', target.getAttribute('data-usage-surface') || 'workspace',
        target.getAttribute('data-usage-action'), { input: event.type,
          restricted: target.matches('.not-allowed-functionality,[aria-disabled="true"],:disabled') });
      return;
    }
    const control = event.target.closest('button,a,input,select,textarea,[role="button"]');
    const element = typeof root === 'function' ? root() : root;
    if (!control || !((element && element.contains(control)) || control.closest('.legacy-overlay-host'))) return;
    const panels = readPanels();
    const chartDialog = control.closest('[data-usage-dialog="chart"]');
    const surface = chartDialog ? 'chart_dialog' : control.closest('#sidebar-content') ? 'right' : control.closest('.legacy-overlay-host') ? 'dialog' : 'workspace';
    const tool = chartDialog ? panels.chart_dialog : surface === 'right' ? panels.right : surface === 'dialog' ? panels.header_dialog || panels.left_dialog || panels.left : 'control';
    profiler.track('interaction', surface, tool || 'control', { input: event.type, control: control.tagName.toLowerCase() });

  });
  const repository = createUsageRepository(uid);
  repository.start().then(enabled => {
    if (disposed || !enabled) return;
    profiler = createUsageProfiler({ experience, send: repository.send, autoFlush: true });
    pendingDiagnostics.forEach(args => profiler.track(...args)); pendingDiagnostics = [];
    profiler.visibility(!document.hidden);
    refresh();
    ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'].forEach(name => listen(document, name, safe(event => {
      const element = typeof root === 'function' ? root() : root;
      if (element && element.contains(event.target)) activity();
    })));
    // Portal dialogs are explicitly annotated too. There is only one active workspace.
    listen(document, 'click', interaction);
    ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'].forEach(name => listen(document, name, safe(event => {
      if (event.target.closest && event.target.closest('.legacy-overlay-host')) activity();
    })));
    listen(document, 'change', interaction);
    listen(document, 'visibilitychange', safe(() => { profiler.visibility(!document.hidden); profiler.flush(true); }));
    listen(window, 'pagehide', safe(() => { profiler.visibility(false); profiler.flush(true); }));
    listen(window, 'pageshow', safe(() => profiler.visibility(!document.hidden)));
    timer = setInterval(safe(() => { refresh(); profiler.tick(); if (++ticks % 2 === 0) profiler.flush(); }), 15000);
    if (onReady) onReady(profiler);
  }).catch(() => {});
  return {
    refresh,
    track: safe((...args) => {
      if (disposed) return;
      if (profiler) {
        profiler.track(...args);
        if (args[0] === 'diagnostic') setTimeout(safe(() => { if (!disposed) profiler.flush(); }), 0);
      } else if (args[0] === 'diagnostic') {
        if (pendingDiagnostics.length >= 20) pendingDiagnostics.shift();
        pendingDiagnostics.push(args);
      }
    }),
    panel: safe((...args) => { if (profiler) profiler.panel(...args); }),
    activity,
    stop(options) {
      disposed = true; pendingDiagnostics = []; clearInterval(timer); cleanups.forEach(fn => fn());
      if (profiler) profiler.stop(options);
    }
  };
}
